// Web Worker for package processing

const ORDER_ITEM_TYPES = Object.freeze({
  TICKET: 'ticket',
  ITEM: 'item'
})

import _ from 'lodash'

export const CONSUMER_MESSAGE_TYPES = Object.freeze({
  INITIALIZE: 'INITIALIZE',
  PROCESS_ITEMS: 'PROCESS_ITEMS',
  PACKAGE_PROCESSED: 'PACKAGE_PROCESSED',
  EXCLUDE_PACKAGE: 'EXCLUDE_PACKAGE',
  UPDATE_PACKAGE_ASSIGNMENTS: 'UPDATE_PACKAGE_ASSIGNMENTS'
})

export const PRODUCER_MESSAGE_TYPES = Object.freeze({
  APPLY_PACKAGE: 'APPLY_PACKAGE',
  APPLY_DISCOUNT: 'APPLY_DISCOUNT'
})

const cache = {
  orderItemsToAdd: [],
  orderItemsToRemove: [],
  availableVariationOrderItems: new Map(),
  availableTicketTypeOrderItems: new Map()
}
let configurationCache = {}

function processItems () {
  const { orderItemsToAdd, orderItemsToRemove } = cache

  const copyOfOrderItemsToAdd = [...orderItemsToAdd]
  const copyOfOrderItemsToRemove = [...orderItemsToRemove]

  cache.orderItemsToAdd = []
  cache.orderItemsToRemove = []

  _.each(copyOfOrderItemsToAdd, orderItem => {
    const availableOrderItems = orderItem.type === ORDER_ITEM_TYPES.TICKET ? cache.availableTicketTypeOrderItems : cache.availableVariationOrderItems

    if (availableOrderItems.has(orderItem.typeId)) {
      availableOrderItems.get(orderItem.typeId).add(orderItem)
    } else {
      availableOrderItems.set(orderItem.typeId, new Set([orderItem]))
    }
  })

  _.each(copyOfOrderItemsToRemove, orderItem => {
    const availableOrderItems = orderItem.type === ORDER_ITEM_TYPES.TICKET ? cache.availableTicketTypeOrderItems : cache.availableVariationOrderItems

    if (availableOrderItems.has(orderItem.typeId)) {
      const availableOrderItem = Array.from(availableOrderItems.get(orderItem.typeId).values()).find(availableOrderItem => availableOrderItem.id === orderItem.id)
      if (availableOrderItem) {
        availableOrderItems.get(orderItem.typeId).delete(availableOrderItem)
      }
    }
  })

  const sortedPackages = configurationCache.sortedPackages

  const ticketsToConsume = structuredClone(cache.availableTicketTypeOrderItems)
  const itemVariationsToConsume = structuredClone(cache.availableVariationOrderItems)

  _.each(sortedPackages, packageToApply => {
    if (_.isEmpty(packageToApply.ticketTypeIds) && _.isEmpty(packageToApply.itemVariationIds)) {
      return
    }
    const { packageId, ticketTypeIds, itemVariationIds } = packageToApply

    // Count how many of each type we need
    const ticketTypeCounts = new Map()
    const itemVariationCounts = new Map()

    _.each(ticketTypeIds, id => {
      ticketTypeCounts.set(id, (ticketTypeCounts.get(id) || 0) + 1)
    })
    _.each(itemVariationIds, id => {
      itemVariationCounts.set(id, (itemVariationCounts.get(id) || 0) + 1)
    })

    let canFormPackage = true

    while (canFormPackage) {
    // Check if we have enough items of each type
      const meetsTicketTypeRequirements = ticketTypeCounts.entries().every(([id, count]) => {
        const availableItems = [...(ticketsToConsume.get(id) || [])].filter(orderItem => !orderItem.excludedPackageIds.includes(packageId))
        return availableItems && availableItems.length >= count
      })

      const meetsItemVariationRequirements = itemVariationCounts.entries().every(([id, count]) => {
        const availableItems = [...(itemVariationsToConsume.get(id) || [])].filter(orderItem => !orderItem.excludedPackageIds.includes(packageId))
        return availableItems && availableItems.length >= count
      })

      canFormPackage = meetsTicketTypeRequirements && meetsItemVariationRequirements

      if (canFormPackage) {
        // Get the required number of items for each order item type and form assignable items
        const getAllOrderItemsForAssignment = (counts, consumeableOrderItems) => {
          const orderItems = []
          for (const [id, count] of counts.entries()) {
            const itemOrderItems = Array.from(consumeableOrderItems.get(id)).filter(orderItem => !orderItem.excludedPackageIds.includes(packageId))
            for (let i = 0; i < count; i++) {
              orderItems.push(itemOrderItems[i])
            }
          }
          return orderItems
        }

        const ticketOrderItems = getAllOrderItemsForAssignment(ticketTypeCounts, ticketsToConsume)
        const itemVariationOrderItems = getAllOrderItemsForAssignment(itemVariationCounts, itemVariationsToConsume)

        const orderItemsToAssign = [...ticketOrderItems, ...itemVariationOrderItems]

        // Remove these items from the available pools
        for (const orderItemToAssign of orderItemsToAssign) {
          const availableOrderItems = orderItemToAssign.type === ORDER_ITEM_TYPES.TICKET ? ticketsToConsume : itemVariationsToConsume
          availableOrderItems.get(orderItemToAssign.typeId).delete(orderItemToAssign)
        }

        if (orderItemsToAssign.some(orderItem => orderItem.assignedPackageId !== packageId)) {
        // Send message to main thread to apply this package

          // Look for any items we are assigning that are already assigned to a package
          const existingPackageGroupIds = orderItemsToAssign.filter(orderItem => orderItem.itemPackageIdentifier).map(orderItem => orderItem.itemPackageIdentifier)
          // Get all order items that include that are part of the group we are re-assigning
          // As if one item is part of the group, all items in the group should be unassigned
          const orderItemsToAssignWithExistingPackage = getAllOrderItems().filter(orderItem => !_.isNil(orderItem.itemPackageIdentifier) && existingPackageGroupIds.includes(orderItem.itemPackageIdentifier))
          postMessage({
            messageType: PRODUCER_MESSAGE_TYPES.APPLY_PACKAGE,
            packageId,
            orderItemUnassignIds: orderItemsToAssignWithExistingPackage.map(orderItem => orderItem.id),
            orderItemAssignIds: orderItemsToAssign.map(orderItem => orderItem.id)
          })
        }
      }
    }
  })
}

function getAllOrderItems () {
  const ticketOrderItems = Array.from(cache.availableTicketTypeOrderItems.values()).map(val => Array.from(val.values()))
  const itemVariationOrderItems = Array.from(cache.availableVariationOrderItems.values()).map(val => Array.from(val.values()))
  const allOrderItems = [...ticketOrderItems, ...itemVariationOrderItems].flatMap(items => items)
  return allOrderItems
}

function unassignPackage (orderItem, packageId) {
  if (orderItem.assignedPackageId === packageId) {
    const matchingItems = getAllOrderItems().filter(matchingOrderItem => orderItem.itemPackageIdentifier === matchingOrderItem.itemPackageIdentifier).map(orderItem => orderItem.id)

    postMessage({
      messageType: PRODUCER_MESSAGE_TYPES.APPLY_PACKAGE,
      packageId,
      orderItemUnassignIds: matchingItems,
      orderItemAssignIds: []
    })
  }
}

function handleExcludePackageMessage (e) {
  const { orderItemId, packageId } = e.data

  const orderItem = _.find(getAllOrderItems(), orderItem => orderItem.id === orderItemId)

  if (orderItem) {
    orderItem.excludedPackageIds = [...orderItem.excludedPackageIds, packageId]
    unassignPackage(orderItem, packageId)
  }
}

function handleUpdatePackageAssignmentsMessage (e) {
  const { orderItemsToUnassign, orderItemsToAssign } = e.data

  const existingAppliedPackageIds = getAllOrderItems().filter(orderItem => orderItem.assignedPackageId).map(orderItem => orderItem.assignedPackageId)

  _.each(orderItemsToUnassign, orderItem => {
    const orderItemCache = cacheForOrderItemType(orderItem.type)

    const availableOrderItemsByItemTypeId = orderItemCache.get(orderItem.typeId) || new Set()

    const resolvedOrderItem = availableOrderItemsByItemTypeId.values().find(cacheOrderItem => cacheOrderItem.id === orderItem.id)

    if (resolvedOrderItem) {
      resolvedOrderItem.assignedPackageId = null
      resolvedOrderItem.itemPackageIdentifier = null
    }
  })

  _.each(orderItemsToAssign, orderItem => {
    const orderItemCache = cacheForOrderItemType(orderItem.type)

    const availableOrderItemsByItemTypeId = orderItemCache.get(orderItem.typeId) || new Set()

    const resolvedOrderItem = availableOrderItemsByItemTypeId.values().find(cacheOrderItem => cacheOrderItem.id === orderItem.id)

    if (resolvedOrderItem) {
      resolvedOrderItem.assignedPackageId = orderItem.assignedPackageId
      resolvedOrderItem.itemPackageIdentifier = orderItem.itemPackageIdentifier
    }
  })

  const currentAppliedPackageIds = _.uniq(getAllOrderItems().filter(orderItem => orderItem.assignedPackageId).map(orderItem => orderItem.assignedPackageId))
  const removedPackageIds = _.difference(existingAppliedPackageIds, currentAppliedPackageIds)
  const addedPackageIds = _.difference(currentAppliedPackageIds, existingAppliedPackageIds)

  if (removedPackageIds.length > 0 || addedPackageIds.length > 0) {
    postMessage({
      messageType: PRODUCER_MESSAGE_TYPES.APPLY_DISCOUNT,
      addedPackageIds,
      removedPackageIds
    })
  }

  processItems()
}

function handlePackageProcessedMessage (e) {
  const { orderItemIds, packageId } = e.data

  _.each(getAllOrderItems(), orderItem => {
    if (orderItemIds.includes(orderItem.id)) {
      orderItem.assignedPackageId = packageId
    }
  })
}

function cacheForOrderItemType (orderItemType) {
  if (orderItemType === ORDER_ITEM_TYPES.TICKET) {
    return cache.availableTicketTypeOrderItems
  } else if (orderItemType === ORDER_ITEM_TYPES.ITEM) {
    return cache.availableVariationOrderItems
  }
}

function handleProcessItemsMessage (e) {
  const { orderItemsToAdd, orderItemsToRemove } = e.data

  cache.orderItemsToAdd = [...cache.orderItemsToAdd, ...orderItemsToAdd]
  cache.orderItemsToRemove = [...cache.orderItemsToRemove, ...orderItemsToRemove]
  processItems()
}

function handleInitializeMessage (e) {
  const { packageToItemMap, ticketTypeToPackagesMap, itemVariationToPackagesMap } = e.data
  configurationCache = {
    sortedPackages: getSortedPackagesFromPackageToItemMap(packageToItemMap),
    ticketTypeToPackagesMap,
    itemVariationToPackagesMap
  }
}

function getSortedPackagesFromPackageToItemMap (packageToItemMap) {
  return Array.from(packageToItemMap.entries())
    .map(([packageId, packageData]) => ({ ...packageData, packageId }))
    .sort((packageA, packageB) => packageA.displayOrder - packageB.displayOrder)
}

self.onmessage = async function (e) {
  switch (e.data?.messageType) {
    case CONSUMER_MESSAGE_TYPES.INITIALIZE:
      handleInitializeMessage(e)
      break
    case CONSUMER_MESSAGE_TYPES.PROCESS_ITEMS:
      handleProcessItemsMessage(e)
      break
    case CONSUMER_MESSAGE_TYPES.PACKAGE_PROCESSED:
      handlePackageProcessedMessage(e)
      break
    case CONSUMER_MESSAGE_TYPES.EXCLUDE_PACKAGE:
      handleExcludePackageMessage(e)
      break
    case CONSUMER_MESSAGE_TYPES.UPDATE_PACKAGE_ASSIGNMENTS:
      handleUpdatePackageAssignmentsMessage(e)
      break
    default:
      // Can receive other dom events, ignore them
      break
  }
}
